'use client';

import React, { useCallback, useState } from 'react';
import { DEV_PANEL_TEST_IDS, DEV_PANEL_TITLE_ID } from './DevPanel.consts';
import {
    actionsStyle,
    bodyStyle,
    closeButtonStyle,
    dangerButtonStyle,
    errorStyle,
    headerStyle,
    overlayStyle,
    panelStyle,
    primaryButtonStyle,
    tabActiveStyle,
    tabsStyle,
    tabStyle,
    titleStyle,
} from './DevPanel.style';
import type { IDevPanelMockField, IDevPanelStorage } from './DevPanel.types';
import { parseFields, stringifyStoredMock } from './DevPanel.utils';
import { FontPreviewList } from './components/FontPreviewList';
import { MockJsonField } from './components/MockJsonField';

interface IDevPanelProps {
    onClose: () => void;
    fields: ReadonlyArray<IDevPanelMockField>;
    storage: IDevPanelStorage;
    onAfterSave: () => void;
    onAfterClear: () => void;
    beforeMockFields?: React.ReactNode;
}

type DevPanelTab = 'mocks' | 'fonts';

const stopOverlayClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
};

const buildInitialValues = (
    fields: ReadonlyArray<IDevPanelMockField>,
    storage: IDevPanelStorage,
): Record<string, string> =>
    Object.fromEntries(
        fields.map((field) => [field.id, stringifyStoredMock(storage.get(field.storageKey))]),
    );

export const DevPanel = ({
    onClose,
    fields,
    storage,
    onAfterSave,
    onAfterClear,
    beforeMockFields,
}: IDevPanelProps): React.ReactElement => {
    const [fieldValues, setFieldValues] = useState(() => buildInitialValues(fields, storage));
    const [errorMessage, setErrorMessage] = useState('');
    const [activeTab, setActiveTab] = useState<DevPanelTab>('mocks');

    const handleSave = useCallback((): void => {
        const parsed = parseFields(fields, fieldValues);
        if (parsed.error !== undefined) {
            setErrorMessage(parsed.error);
            return;
        }
        const parsedValues = parsed.values;
        if (parsedValues === undefined) {
            return;
        }
        fields.forEach((field) => {
            const value = parsedValues[field.id];
            if (value !== undefined) {
                storage.set(field.storageKey, value);
            }
        });
        onAfterSave();
    }, [fields, fieldValues, storage, onAfterSave]);

    const handleClear = useCallback((): void => {
        if (!window.confirm('Clear all API mocks?')) {
            return;
        }
        fields.forEach((field) => {
            storage.remove(field.storageKey);
        });
        onAfterClear();
    }, [fields, storage, onAfterClear]);

    const handleSelectMocksTab = useCallback((): void => {
        setActiveTab('mocks');
    }, []);

    const handleSelectFontsTab = useCallback((): void => {
        setActiveTab('fonts');
    }, []);

    const handleFieldChange = useCallback((fieldId: string, value: string): void => {
        setFieldValues((current) => ({ ...current, [fieldId]: value }));
    }, []);

    let mocksTabStyle = tabStyle;
    let fontsTabStyle = tabStyle;
    if (activeTab === 'mocks') {
        mocksTabStyle = tabActiveStyle;
    } else {
        fontsTabStyle = tabActiveStyle;
    }

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={DEV_PANEL_TITLE_ID}
                data-testid={DEV_PANEL_TEST_IDS.root}
                style={panelStyle}
                onClick={stopOverlayClick}
            >
                <div style={headerStyle}>
                    <h2 id={DEV_PANEL_TITLE_ID} style={titleStyle}>
                        Dev Panel
                    </h2>
                    <button
                        data-testid={DEV_PANEL_TEST_IDS.close}
                        type="button"
                        style={closeButtonStyle}
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
                <div style={tabsStyle} role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'mocks'}
                        data-testid={DEV_PANEL_TEST_IDS.tabMocks}
                        style={mocksTabStyle}
                        onClick={handleSelectMocksTab}
                    >
                        API mocks
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'fonts'}
                        data-testid={DEV_PANEL_TEST_IDS.tabFonts}
                        style={fontsTabStyle}
                        onClick={handleSelectFontsTab}
                    >
                        Fonts
                    </button>
                </div>
                <div style={bodyStyle}>
                    {activeTab === 'mocks' && (
                        <React.Fragment>
                            {beforeMockFields}
                            {fields.map((field) => {
                                let value = fieldValues[field.id];
                                if (value === undefined) {
                                    value = '';
                                }
                                return (
                                    <MockJsonField
                                        key={field.id}
                                        label={field.label}
                                        testId={field.testId}
                                        value={value}
                                        onChange={(nextValue) => handleFieldChange(field.id, nextValue)}
                                    />
                                );
                            })}
                            {errorMessage !== '' && (
                                <p data-testid={DEV_PANEL_TEST_IDS.error} style={errorStyle}>
                                    {errorMessage}
                                </p>
                            )}
                        </React.Fragment>
                    )}
                    {activeTab === 'fonts' && <FontPreviewList />}
                </div>
                {activeTab === 'mocks' && (
                    <div style={actionsStyle}>
                        <button
                            data-testid={DEV_PANEL_TEST_IDS.clear}
                            type="button"
                            style={dangerButtonStyle}
                            onClick={handleClear}
                        >
                            Clear
                        </button>
                        <button
                            data-testid={DEV_PANEL_TEST_IDS.save}
                            type="button"
                            style={primaryButtonStyle}
                            onClick={handleSave}
                        >
                            Save
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
